import { ContractError, } from '@platform/contracts';
import { PLATFORM_TENANT } from '@platform/authz';
import { MOCK_TEMP_PASSWORD, membershipsOf, mockPersonalTenantId, personByEmail, personOf, tenantOf, } from "./world.js";
/** The public view of a sign-up, as `GET /signup/status` answers it (registry.ts `toOnboardingStatus`). */
function statusOf(s) {
    return {
        slug: s.slug,
        status: s.status,
        ...(s.status === 'failed' ? { issue: s.issue ?? 'delayed' } : {}),
        ...(s.status === 'pendingPayment' && s.checkoutUrl ? { checkoutUrl: s.checkoutUrl } : {}),
        ...(s.status === 'active' && s.hostname ? { hostname: s.hostname } : {}),
    };
}
const failure = (operationId, status, code, message) => new ContractError(operationId, status, { code, message, requestId: `mock-${Date.now()}` }, 'http');
/**
 * Every operation the screens call, implemented over the mock world with the same rules the
 * real services enforce: a caller acts in ONE tenant (`X-Tenant-Id`, here the active
 * principal); a duplicate invitation is a 409; a package edit publishes a new version and never
 * rewrites a tenant snapshot; staff without PlatformAdmin get a 403; a cross-tenant read is a
 * 404. Operations no screen calls yet answer 501 through `unimplemented`.
 */
export function createMockApiClients(options) {
    const { world } = options;
    const latency = options.latencyMs ?? 250;
    const wait = (value) => latency === 0
        ? Promise.resolve(value)
        : new Promise((resolve) => setTimeout(() => resolve(value), latency));
    const requestId = () => `mock-${world.nextId('r')}`;
    const checkoutOrigin = options.checkoutOrigin ?? 'https://checkout.stripe.example';
    const exportDelay = options.exportReadyAfterMs ?? 2_000;
    const envelope = (tenantId, data) => ({ tenantId, requestId: requestId(), data });
    const page = (tenantId, items) => ({ tenantId, requestId: requestId(), items });
    const tenantCaller = (op) => {
        const p = options.getPrincipal();
        if (!p)
            throw failure(op, 401, 'unauthenticated', 'Sign in first');
        if (!p.tenantId || p.tenantId === PLATFORM_TENANT)
            throw failure(op, 401, 'unauthenticated', 'Choose a workspace first');
        if (!membershipsOf(world, p.sub).some((m) => m.tenantId === p.tenantId))
            throw failure(op, 403, 'forbidden', 'You are not a member of this workspace');
        return { principal: p, tenantId: p.tenantId };
    };
    const tenantAdmin = (op) => {
        const c = tenantCaller(op);
        if (!c.principal.roles.includes('TenantAdmin'))
            throw failure(op, 403, 'forbidden', 'Only a workspace admin can do that');
        return c;
    };
    const platformAdmin = (op) => {
        const p = options.getPrincipal();
        if (!p)
            throw failure(op, 401, 'unauthenticated', 'Sign in first');
        if (p.tenantId !== PLATFORM_TENANT || !p.roles.includes('PlatformAdmin'))
            throw failure(op, 403, 'forbidden', 'Platform admin only');
        return p;
    };
    const memberOf = (m) => {
        const person = personOf(world, m.sub);
        return {
            sub: m.sub,
            email: person?.email ?? m.sub,
            ...(person?.name ? { displayName: person.name } : {}),
            roles: [...m.roles],
            ...(m.officeCode ? { officeCode: m.officeCode } : {}),
            ...(m.agentCode ? { agentCode: m.agentCode } : {}),
            grants: [...m.grants],
            status: m.status,
            createdAt: m.createdAt,
        };
    };
    const membershipOf = (m) => ({
        tenantId: m.tenantId,
        tenantName: m.tenantName,
        tier: m.tier,
        sub: m.sub,
        roles: [...m.roles],
        ...(m.officeCode ? { officeCode: m.officeCode } : {}),
        ...(m.agentCode ? { agentCode: m.agentCode } : {}),
        grants: [...m.grants],
        createdAt: m.createdAt,
    });
    /** `GET /me`: the caller, their membership, the documents still due and how they want notices. */
    const meOf = (principal, tenantId, m) => {
        const declared = world.legalDocuments?.[tenantOf(world, tenantId)?.product ?? ''] ?? [];
        const consent = world.consents?.[principal.sub];
        const due = consent ? declared.filter((d) => consent.documents[d.document] !== d.version) : [];
        return {
            sub: principal.sub,
            email: principal.email,
            displayName: principal.name,
            current: membershipOf(m),
            ...(due.length > 0 ? { documentsDue: due.map((d) => ({ ...d })) } : {}),
            ...(consent?.noticeChannel ? { noticeChannel: consent.noticeChannel } : {}),
        };
    };
    const identityClient = mockClient('identity', {
        Self_get: async () => {
            const { principal, tenantId } = tenantCaller('Self_get');
            const m = world.memberships.find((x) => x.sub === principal.sub && x.tenantId === tenantId);
            if (!m)
                throw failure('Self_get', 404, 'notFound', 'Not found');
            // Reading `me` for the first time is what flips an invitation to accepted (identity rule 4).
            m.status = 'active';
            return wait(envelope(tenantId, meOf(principal, tenantId, m)));
        },
        // A later acceptance (ADR tenant-export-deletion-and-consent): only the current versions count.
        Self_consent: async ({ body }) => {
            const { principal, tenantId } = tenantCaller('Self_consent');
            const m = world.memberships.find((x) => x.sub === principal.sub && x.tenantId === tenantId);
            if (!m)
                throw failure('Self_consent', 404, 'notFound', 'Not found');
            const declared = world.legalDocuments?.[tenantOrFail('Self_consent', tenantId).product] ?? [];
            if (body.accepted.some((a) => !declared.some((d) => d.document === a.document && d.version === a.version)))
                throw failure('Self_consent', 409, 'conflict', 'The documents to accept have changed');
            const entry = (world.consents ??= {})[principal.sub] ?? { documents: {} };
            for (const a of body.accepted)
                entry.documents[a.document] = a.version;
            if (body.noticeChannel)
                entry.noticeChannel = body.noticeChannel;
            world.consents[principal.sub] = entry;
            return wait(envelope(tenantId, meOf(principal, tenantId, m)));
        },
        Self_memberships: async () => {
            const { principal, tenantId } = tenantCaller('Self_memberships');
            return wait(page(tenantId, membershipsOf(world, principal.sub).map(membershipOf)));
        },
        Members_list: async () => {
            const { tenantId } = tenantCaller('Members_list');
            return wait(page(tenantId, world.memberships.filter((m) => m.tenantId === tenantId).map(memberOf)));
        },
        Members_get: async ({ params }) => {
            const { tenantId } = tenantCaller('Members_get');
            const m = world.memberships.find((x) => x.tenantId === tenantId && x.sub === params.sub);
            if (!m)
                throw failure('Members_get', 404, 'notFound', 'Not found');
            return wait(envelope(tenantId, memberOf(m)));
        },
        Members_update: async ({ params, body }) => {
            const { tenantId } = tenantAdmin('Members_update');
            const m = world.memberships.find((x) => x.tenantId === tenantId && x.sub === params.sub);
            if (!m)
                throw failure('Members_update', 404, 'notFound', 'Not found');
            if (body.roles)
                m.roles = [...body.roles];
            if (body.officeCode !== undefined)
                m.officeCode = body.officeCode;
            if (body.agentCode !== undefined)
                m.agentCode = body.agentCode;
            if (body.grants)
                m.grants = [...body.grants];
            return wait(envelope(tenantId, memberOf(m)));
        },
        Members_remove: async ({ params }) => {
            const { principal, tenantId } = tenantAdmin('Members_remove');
            if (params.sub === principal.sub)
                throw failure('Members_remove', 403, 'forbidden', 'You cannot remove yourself');
            const before = world.memberships.length;
            world.memberships = world.memberships.filter((x) => !(x.tenantId === tenantId && x.sub === params.sub));
            if (world.memberships.length === before)
                throw failure('Members_remove', 404, 'notFound', 'Not found');
            return wait(undefined);
        },
        Invitations_list: async ({ query } = {}) => {
            const { tenantId } = tenantAdmin('Invitations_list');
            const items = world.invitations.filter((i) => i.tenantId === tenantId && (!query?.status || i.status === query.status));
            return wait(page(tenantId, items));
        },
        Invitations_create: async ({ body }) => {
            const { principal, tenantId } = tenantAdmin('Invitations_create');
            const email = body.email.trim().toLowerCase();
            const existing = personByEmail(world, email);
            if (existing && membershipsOf(world, existing.sub).some((m) => m.tenantId === tenantId))
                throw failure('Invitations_create', 409, 'conflict', 'That email already belongs to this workspace');
            if (world.invitations.some((i) => i.tenantId === tenantId && i.email === email && i.status === 'pending'))
                throw failure('Invitations_create', 409, 'conflict', 'That person already has a pending invitation');
            const now = new Date();
            const sub = existing?.sub ?? `mock|${world.nextId('u')}`;
            if (!existing) {
                world.people.push({
                    sub,
                    email,
                    name: email.split('@')[0] ?? email,
                    password: MOCK_TEMP_PASSWORD,
                    invited: true,
                    totpEnrolled: false,
                    staff: false,
                    platformAdmin: false,
                });
            }
            const home = tenantOf(world, tenantId);
            world.memberships.push({
                tenantId,
                tenantName: home?.name ?? tenantId,
                tier: home?.tier ?? 'pool',
                sub,
                roles: [...body.roles],
                ...(body.officeCode ? { officeCode: body.officeCode } : {}),
                ...(body.agentCode ? { agentCode: body.agentCode } : {}),
                grants: [],
                createdAt: now.toISOString(),
                status: 'invited',
            });
            const invitation = {
                invitationId: ulidLike(world),
                tenantId,
                email,
                roles: [...body.roles],
                ...(body.officeCode ? { officeCode: body.officeCode } : {}),
                ...(body.agentCode ? { agentCode: body.agentCode } : {}),
                status: 'pending',
                invitedBy: principal.sub,
                expiresAt: new Date(now.getTime() + 3 * 86_400_000).toISOString(),
                createdAt: now.toISOString(),
            };
            world.invitations.push(invitation);
            return wait(envelope(tenantId, invitation));
        },
        Invitations_get: async ({ params }) => {
            const { tenantId } = tenantAdmin('Invitations_get');
            const i = world.invitations.find((x) => x.tenantId === tenantId && x.invitationId === params.invitationId);
            if (!i)
                throw failure('Invitations_get', 404, 'notFound', 'Not found');
            return wait(envelope(tenantId, i));
        },
        Invitations_revoke: async ({ params }) => {
            const { tenantId } = tenantAdmin('Invitations_revoke');
            const i = world.invitations.find((x) => x.tenantId === tenantId && x.invitationId === params.invitationId);
            if (!i)
                throw failure('Invitations_revoke', 404, 'notFound', 'Not found');
            if (i.status !== 'pending')
                throw failure('Invitations_revoke', 409, 'conflict', 'That invitation is no longer pending');
            i.status = 'revoked';
            world.memberships = world.memberships.filter((m) => !(m.tenantId === tenantId &&
                m.status === 'invited' &&
                personOf(world, m.sub)?.email === i.email));
            return wait(envelope(tenantId, i));
        },
        Invitations_resend: async ({ params }) => {
            const { tenantId } = tenantAdmin('Invitations_resend');
            const i = world.invitations.find((x) => x.tenantId === tenantId && x.invitationId === params.invitationId);
            if (!i)
                throw failure('Invitations_resend', 404, 'notFound', 'Not found');
            if (i.status !== 'pending')
                throw failure('Invitations_resend', 409, 'conflict', 'That invitation is no longer pending');
            i.expiresAt = new Date(Date.now() + 3 * 86_400_000).toISOString();
            return wait(envelope(tenantId, i));
        },
    });
    const packageOrFail = (op, packageId) => {
        const pkg = world.packages.find((p) => p.packageId === packageId);
        if (!pkg)
            throw failure(op, 404, 'notFound', 'No such package');
        return pkg;
    };
    const tenantOrFail = (op, tenantId) => {
        const t = tenantOf(world, tenantId);
        if (!t)
            throw failure(op, 404, 'notFound', 'No such tenant');
        return t;
    };
    const touch = (t) => {
        t.updatedAt = new Date().toISOString();
        return t;
    };
    const toTenant = (t) => ({
        tenantId: t.tenantId,
        slug: t.slug,
        product: t.product,
        name: t.name,
        tier: t.tier,
        status: t.status,
        cellId: t.cellId,
        adminEmail: t.adminEmail,
        ...(t.entitlement
            ? { entitlement: { ...t.entitlement, features: [...t.entitlement.features] } }
            : {}),
        flags: { ...t.flags },
        createdAt: t.createdAt,
        updatedAt: t.updatedAt,
    });
    const platformClient = mockClient('platform', {
        Tenants_list: async ({ query } = {}) => {
            platformAdmin('Tenants_list');
            const items = world.tenants
                .filter((t) => !query?.status || t.status === query.status)
                .map(toTenant);
            return wait(page(PLATFORM_TENANT, items));
        },
        Tenants_get: async ({ params }) => {
            platformAdmin('Tenants_get');
            return wait(envelope(PLATFORM_TENANT, toTenant(tenantOrFail('Tenants_get', params.tenantId))));
        },
        Tenants_suspend: async ({ params }) => {
            platformAdmin('Tenants_suspend');
            const t = tenantOrFail('Tenants_suspend', params.tenantId);
            if (t.status === 'suspended')
                throw failure('Tenants_suspend', 409, 'conflict', 'Already suspended');
            t.status = 'suspended';
            return wait(envelope(PLATFORM_TENANT, toTenant(touch(t))));
        },
        Tenants_resume: async ({ params }) => {
            platformAdmin('Tenants_resume');
            const t = tenantOrFail('Tenants_resume', params.tenantId);
            if (t.status !== 'suspended')
                throw failure('Tenants_resume', 409, 'conflict', 'Not suspended');
            t.status = 'active';
            return wait(envelope(PLATFORM_TENANT, toTenant(touch(t))));
        },
        Tenants_adoptPackage: async ({ params, body }) => {
            platformAdmin('Tenants_adoptPackage');
            const t = tenantOrFail('Tenants_adoptPackage', params.tenantId);
            const pkg = packageOrFail('Tenants_adoptPackage', body.packageId);
            if (pkg.retired)
                throw failure('Tenants_adoptPackage', 409, 'conflict', 'That package is retired');
            if (!pkg.tiers.includes(t.tier))
                throw failure('Tenants_adoptPackage', 409, 'conflict', `That package is not offered on the ${t.tier} tier`);
            t.entitlement = {
                packageId: pkg.packageId,
                packageVersion: pkg.version,
                features: [...pkg.features],
                stripePriceId: pkg.stripePriceId,
                ...(pkg.assistant ? { assistant: structuredClone(pkg.assistant) } : {}),
                snapshotAt: new Date().toISOString(),
            };
            return wait(envelope(PLATFORM_TENANT, toTenant(touch(t))));
        },
        Tenants_reapplyPackage: async ({ params }) => {
            platformAdmin('Tenants_reapplyPackage');
            const t = tenantOrFail('Tenants_reapplyPackage', params.tenantId);
            if (!t.entitlement)
                throw failure('Tenants_reapplyPackage', 409, 'conflict', 'The tenant has no package');
            const pkg = packageOrFail('Tenants_reapplyPackage', t.entitlement.packageId);
            t.entitlement = {
                packageId: pkg.packageId,
                packageVersion: pkg.version,
                features: [...pkg.features],
                stripePriceId: pkg.stripePriceId,
                ...(pkg.assistant ? { assistant: structuredClone(pkg.assistant) } : {}),
                snapshotAt: new Date().toISOString(),
            };
            return wait(envelope(PLATFORM_TENANT, toTenant(touch(t))));
        },
        Packages_list: async ({ query } = {}) => {
            platformAdmin('Packages_list');
            const items = world.packages
                .filter((p) => query?.includeRetired || !p.retired)
                .map((p) => ({ ...p }));
            return wait(page(PLATFORM_TENANT, items));
        },
        Metering_usage: async ({ query }) => {
            platformAdmin('Metering_usage');
            const day = query.period.length === 10;
            const items = world.tenants
                .filter((t) => MOCK_USAGE[t.tenantId] && (!query.tenantId || query.tenantId === t.tenantId))
                .map((t) => {
                const usage = MOCK_USAGE[t.tenantId];
                return {
                    tenantId: t.tenantId,
                    period: query.period,
                    metrics: day ? usage.day : usage.month,
                };
            });
            return wait(page(PLATFORM_TENANT, items));
        },
        Packages_get: async ({ params }) => {
            platformAdmin('Packages_get');
            return wait(envelope(PLATFORM_TENANT, { ...packageOrFail('Packages_get', params.packageId) }));
        },
        Packages_create: async ({ body }) => {
            platformAdmin('Packages_create');
            if (world.packages.some((p) => p.packageId === body.packageId))
                throw failure('Packages_create', 409, 'conflict', 'A package with that id already exists');
            const now = new Date().toISOString();
            const pkg = {
                ...body,
                billingModel: body.billingModel ?? 'perSeat',
                version: 1,
                retired: false,
                createdAt: now,
                updatedAt: now,
            };
            world.packages.push(pkg);
            return wait(envelope(PLATFORM_TENANT, { ...pkg }));
        },
        Packages_update: async ({ params, body }) => {
            platformAdmin('Packages_update');
            const pkg = packageOrFail('Packages_update', params.packageId);
            // A new version of the catalog entry; every tenant snapshot stays exactly as it was.
            if (body.name !== undefined)
                pkg.name = body.name;
            if (body.tiers !== undefined)
                pkg.tiers = [...body.tiers];
            if (body.features !== undefined)
                pkg.features = [...body.features];
            if (body.stripePriceId !== undefined)
                pkg.stripePriceId = body.stripePriceId;
            if (body.billingModel !== undefined)
                pkg.billingModel = body.billingModel;
            if (body.assistant === null)
                delete pkg.assistant;
            else if (body.assistant !== undefined)
                pkg.assistant = structuredClone(body.assistant);
            pkg.version += 1;
            pkg.updatedAt = new Date().toISOString();
            return wait(envelope(PLATFORM_TENANT, { ...pkg }));
        },
        Packages_retire: async ({ params }) => {
            platformAdmin('Packages_retire');
            const pkg = packageOrFail('Packages_retire', params.packageId);
            if (pkg.retired)
                throw failure('Packages_retire', 409, 'conflict', 'Already retired');
            pkg.retired = true;
            pkg.updatedAt = new Date().toISOString();
            return wait(envelope(PLATFORM_TENANT, { ...pkg }));
        },
        Products_list: async () => {
            platformAdmin('Products_list');
            return wait(page(PLATFORM_TENANT, [
                {
                    name: 'sprocket',
                    displayName: 'Sprocket',
                    tenantShape: 'organization',
                    tiers: ['pool', 'silo'],
                },
                {
                    name: 'mymedmetrics',
                    displayName: 'MyMedMetrics',
                    tenantShape: 'personal',
                    tiers: ['pool'],
                },
            ]));
        },
        Tiers_list: async () => {
            platformAdmin('Tiers_list');
            return wait(page(PLATFORM_TENANT, [
                {
                    tier: 'pool',
                    description: 'Shared cell',
                    rateLimitPerSecond: 50,
                    burstLimit: 100,
                    dedicatedCell: false,
                },
                {
                    tier: 'silo',
                    description: 'Dedicated cell',
                    rateLimitPerSecond: 500,
                    burstLimit: 1000,
                    dedicatedCell: true,
                },
            ]));
        },
        Workspaces_get: async () => {
            const { tenantId } = tenantCaller('Workspaces_get');
            const t = tenantOrFail('Workspaces_get', tenantId);
            return wait(envelope(tenantId, {
                tenantId: t.tenantId,
                slug: t.slug,
                name: t.name,
                tier: t.tier,
                status: t.status,
                ...(t.entitlement ? { entitlement: { ...t.entitlement } } : {}),
                flags: { ...t.flags },
                ...(t.deletion && !t.deletion.cancelledAt
                    ? {
                        deletion: {
                            requestedAt: t.deletion.requestedAt,
                            purgeAt: t.deletion.purgeAt,
                        },
                    }
                    : {}),
            }));
        },
        // Export (ADR tenant-export-deletion-and-consent): a TenantAdmin's own copy, ready a moment
        // after the request, with a short-lived link.
        WorkspaceExports_create: async () => {
            const { tenantId } = tenantAdmin('WorkspaceExports_create');
            const now = Date.now();
            const list = (world.exports ??= []);
            if (list.some((e) => e.tenantId === tenantId && now - e.requestedMs < exportDelay))
                throw failure('WorkspaceExports_create', 409, 'conflict', 'An export is being prepared');
            const record = {
                exportId: ulidLike(world),
                tenantId,
                requestedAt: new Date(now).toISOString(),
                requestedMs: now,
            };
            list.push(record);
            return wait(envelope(tenantId, {
                exportId: record.exportId,
                status: 'pending',
                requestedAt: record.requestedAt,
                expiresAt: new Date(now + 7 * DAY_MS).toISOString(),
            }));
        },
        WorkspaceExports_get: async ({ params }) => {
            const { tenantId } = tenantAdmin('WorkspaceExports_get');
            const record = world.exports?.find((e) => e.tenantId === tenantId && e.exportId === params.exportId);
            if (!record)
                throw failure('WorkspaceExports_get', 404, 'notFound', 'Not found');
            const now = Date.now();
            const base = {
                exportId: record.exportId,
                requestedAt: record.requestedAt,
                expiresAt: new Date(record.requestedMs + 7 * DAY_MS).toISOString(),
            };
            const ready = now - record.requestedMs >= exportDelay;
            return wait(envelope(tenantId, ready
                ? {
                    ...base,
                    status: 'ready',
                    readyAt: new Date(record.requestedMs + exportDelay).toISOString(),
                    downloadUrl: `https://exports.example.test/${record.exportId}.zip`,
                    downloadExpiresAt: new Date(now + 5 * 60_000).toISOString(),
                }
                : { ...base, status: 'pending' }));
        },
        // Deletion: 30 days' grace, withdrawn any time before it passes.
        WorkspaceDeletions_request: async () => {
            const { tenantId } = tenantAdmin('WorkspaceDeletions_request');
            const t = tenantOrFail('WorkspaceDeletions_request', tenantId);
            if (!t.deletion || t.deletion.cancelledAt) {
                const now = Date.now();
                t.deletion = {
                    requestedAt: new Date(now).toISOString(),
                    purgeAt: new Date(now + 30 * DAY_MS).toISOString(),
                };
            }
            return wait(envelope(tenantId, {
                requestedAt: t.deletion.requestedAt,
                purgeAt: t.deletion.purgeAt,
            }));
        },
        WorkspaceDeletions_cancel: async () => {
            const { tenantId } = tenantAdmin('WorkspaceDeletions_cancel');
            const t = tenantOrFail('WorkspaceDeletions_cancel', tenantId);
            if (!t.deletion || t.deletion.cancelledAt)
                throw failure('WorkspaceDeletions_cancel', 404, 'notFound', 'Nothing is scheduled');
            t.deletion = { ...t.deletion, cancelledAt: new Date().toISOString() };
            return wait(undefined);
        },
        // The staff console's announcements: published to one tenant (or person) or a cell's active
        // tenants; each tenant gets a notice its people see in the app.
        Announcements_list: async () => {
            platformAdmin('Announcements_list');
            return wait(page(PLATFORM_TENANT, [...(world.announcements ?? [])].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt))));
        },
        Announcements_publish: async ({ body }) => {
            const staff = platformAdmin('Announcements_publish');
            const { tenantId, cellId, sub } = body.target;
            if ((tenantId === undefined) === (cellId === undefined) || (sub && !tenantId))
                throw failure('Announcements_publish', 400, 'validation', 'Name one tenant or one cell');
            const targets = tenantId
                ? [tenantOrFail('Announcements_publish', tenantId)]
                : world.tenants.filter((t) => t.cellId === cellId && t.status === 'active');
            if (targets.length === 0)
                throw failure('Announcements_publish', 409, 'conflict', 'No tenant to show it to');
            const publishedAt = new Date().toISOString();
            const announcementId = `n_${world.nextId('mock')}x${Date.now().toString(36)}`.padEnd(14, '0');
            const announcement = {
                announcementId,
                target: body.target,
                severity: body.severity,
                title: body.title,
                body: body.body,
                ...(body.link ? { link: body.link } : {}),
                publishedAt,
                ...(body.expiresAt ? { expiresAt: body.expiresAt } : {}),
                publishedBy: staff.sub,
                tenantCount: targets.length,
            };
            (world.announcements ??= []).push(announcement);
            const notices = (world.notices ??= []);
            for (const t of targets)
                notices.push({
                    noticeId: announcementId,
                    announcementId,
                    tenantId: t.tenantId,
                    severity: body.severity,
                    title: body.title,
                    body: body.body,
                    ...(body.link ? { link: body.link } : {}),
                    publishedAt,
                    ...(body.expiresAt ? { expiresAt: body.expiresAt } : {}),
                    ...(sub ? { sub } : {}),
                    acks: {},
                });
            return wait(envelope(PLATFORM_TENANT, announcement));
        },
        Announcements_withdraw: async ({ params }) => {
            platformAdmin('Announcements_withdraw');
            const a = world.announcements?.find((x) => x.announcementId === params.announcementId);
            if (!a)
                throw failure('Announcements_withdraw', 404, 'notFound', 'No such announcement');
            if (!a.withdrawnAt) {
                a.withdrawnAt = new Date().toISOString();
                for (const n of world.notices ?? [])
                    if (n.announcementId === a.announcementId)
                        n.withdrawnAt = a.withdrawnAt;
            }
            return wait(envelope(PLATFORM_TENANT, a));
        },
        Workspaces_entitlement: async () => {
            const { tenantId } = tenantCaller('Workspaces_entitlement');
            const t = tenantOrFail('Workspaces_entitlement', tenantId);
            if (!t.entitlement)
                throw failure('Workspaces_entitlement', 404, 'notFound', 'No package yet');
            return wait(envelope(tenantId, { ...t.entitlement }));
        },
        Workspaces_catalog: async () => {
            const { tenantId } = tenantCaller('Workspaces_catalog');
            const t = tenantOrFail('Workspaces_catalog', tenantId);
            const items = world.packages
                .filter((p) => !p.retired && p.tiers.includes(t.tier))
                .map((p) => ({
                packageId: p.packageId,
                name: p.name,
                features: [...p.features],
                version: p.version,
            }));
            return wait(page(tenantId, items));
        },
        Signup_catalog: async ({ query } = {}) => wait(page(PLATFORM_TENANT, world.packages
            .filter((p) => !p.retired &&
            p.product === (query?.product ?? 'sprocket') &&
            p.tiers.includes('pool'))
            .map((p) => ({
            packageId: p.packageId,
            name: p.name,
            features: [...p.features],
            version: p.version,
        })))),
        Signup_availability: async ({ query }) => {
            const taken = world.tenants.some((t) => t.slug === query.slug) || world.signups.has(query.slug);
            return wait(envelope(PLATFORM_TENANT, {
                slug: query.slug,
                available: !taken && !RESERVED.has(query.slug),
            }));
        },
        Signup_request: async ({ body }) => {
            if (world.tenants.some((t) => t.slug === body.slug) || RESERVED.has(body.slug))
                throw failure('Signup_request', 409, 'conflict', 'That workspace address is taken');
            const pkg = packageOrFail('Signup_request', body.packageId);
            const status = { slug: body.slug, status: 'provisioning' };
            world.signups.set(body.slug, { ...status, startedAt: Date.now() });
            // Provision "in the background": the tenant appears, locked, with the first admin invited.
            setTimeout(() => {
                const now = new Date().toISOString();
                const tenant = {
                    tenantId: body.slug,
                    slug: body.slug,
                    product: pkg.product,
                    name: body.workspaceName,
                    tier: 'pool',
                    status: 'locked',
                    cellId: 'shared-1',
                    adminEmail: body.email,
                    entitlement: {
                        packageId: pkg.packageId,
                        packageVersion: pkg.version,
                        features: [...pkg.features],
                        stripePriceId: pkg.stripePriceId,
                        snapshotAt: now,
                    },
                    flags: {},
                    createdAt: now,
                    updatedAt: now,
                    billing: {
                        status: 'none',
                        packageId: pkg.packageId,
                        seats: { licensed: 1, used: 1 },
                        billingModel: 'flat',
                        cancelAtPeriodEnd: false,
                        portalAvailable: false,
                    },
                };
                world.tenants.push(tenant);
                const email = body.email.trim().toLowerCase();
                const existing = personByEmail(world, email);
                const sub = existing?.sub ?? `mock|${world.nextId('u')}`;
                if (!existing)
                    world.people.push({
                        sub,
                        email,
                        name: email.split('@')[0] ?? email,
                        password: MOCK_TEMP_PASSWORD,
                        invited: true,
                        totpEnrolled: false,
                        staff: false,
                        platformAdmin: false,
                    });
                world.memberships.push({
                    tenantId: body.slug,
                    tenantName: body.workspaceName,
                    tier: 'pool',
                    sub,
                    roles: ['TenantAdmin'],
                    grants: [],
                    createdAt: now,
                    status: 'invited',
                });
                world.signups.set(body.slug, {
                    slug: body.slug,
                    status: 'active',
                    startedAt: Date.now(),
                });
            }, Math.max(latency * 4, 1));
            return wait(envelope(PLATFORM_TENANT, status));
        },
        Signup_status: async ({ query }) => {
            const s = world.signups.get(query.slug);
            if (!s)
                throw failure('Signup_status', 404, 'notFound', 'No sign-up for that address');
            return wait(envelope(PLATFORM_TENANT, statusOf(s)));
        },
        // Personal sign-up (ADR personal-tenants-sign-up-in-the-cell-pool): who signs up comes from
        // the session, never the body; the tenant id is derived from product + person, so a repeat
        // resumes it; billing "creates" the checkout a moment later, as the real workflow does.
        // The documents a product asks people to accept (none unless the world declares some).
        Signup_documents: async ({ query }) => wait(page(PLATFORM_TENANT, [...(world.legalDocuments?.[query.product] ?? [])])),
        Signup_personal: async ({ body }) => {
            const p = options.getPrincipal();
            const person = p ? personOf(world, p.sub) : undefined;
            if (!p || !person || person.unconfirmed)
                throw failure('Signup_personal', 401, 'unauthenticated', 'Sign in first');
            const pkg = packageOrFail('Signup_personal', body.packageId);
            // The control plane's consent rules: a stale version is a 409, a missing document or the
            // missing email notice choice a 400.
            const declared = world.legalDocuments?.[pkg.product] ?? [];
            const sent = body.accepted ?? [];
            if (sent.some((a) => !declared.some((d) => d.document === a.document && d.version === a.version)))
                throw failure('Signup_personal', 409, 'conflict', 'The documents to accept have changed');
            if (declared.length > 0 &&
                (declared.some((d) => !sent.some((a) => a.document === d.document && a.version === d.version)) ||
                    body.noticeChannel !== 'email'))
                throw failure('Signup_personal', 400, 'validation', 'Accept every document to sign up');
            if (!world.personalProduct ||
                pkg.product !== world.personalProduct ||
                pkg.billingModel !== 'flat' ||
                pkg.retired)
                throw failure('Signup_personal', 409, 'conflict', 'Package cannot be adopted (not a personal product)');
            if (declared.length > 0)
                (world.consents ??= {})[p.sub] = {
                    documents: Object.fromEntries(sent.map((a) => [a.document, a.version])),
                    noticeChannel: 'email',
                };
            const tenantId = mockPersonalTenantId(pkg.product, p.sub);
            const existing = world.signups.get(tenantId);
            if (existing && existing.status !== 'failed')
                return wait(envelope(PLATFORM_TENANT, statusOf(existing)));
            const now = new Date().toISOString();
            if (!tenantOf(world, tenantId))
                world.tenants.push({
                    tenantId,
                    slug: tenantId,
                    product: pkg.product,
                    name: person.name,
                    tier: 'pool',
                    status: 'pendingPayment',
                    cellId: `${pkg.product}-1`,
                    adminEmail: person.email,
                    entitlement: {
                        packageId: pkg.packageId,
                        packageVersion: pkg.version,
                        features: [...pkg.features],
                        stripePriceId: pkg.stripePriceId,
                        snapshotAt: now,
                    },
                    flags: {},
                    createdAt: now,
                    updatedAt: now,
                    billing: {
                        status: 'none',
                        packageId: pkg.packageId,
                        seats: { licensed: 1, used: 1 },
                        billingModel: 'flat',
                        cancelAtPeriodEnd: false,
                        portalAvailable: false,
                    },
                });
            const entry = {
                slug: tenantId,
                status: 'pendingPayment',
                startedAt: Date.now(),
                ownerSub: p.sub,
            };
            world.signups.set(tenantId, entry);
            const sessionId = `cs_mock_${world.nextId('s')}`;
            const url = new URL(`${checkoutOrigin}/pay`);
            url.searchParams.set('tenant', tenantId);
            url.searchParams.set('seats', '1');
            url.searchParams.set('return', `/signup/complete?slug=${encodeURIComponent(tenantId)}&session_id=${sessionId}`);
            url.searchParams.set('cancel', `/signup?slug=${encodeURIComponent(tenantId)}&cancelled=1`);
            setTimeout(() => {
                if (entry.status === 'pendingPayment')
                    entry.checkoutUrl = url.toString();
            }, Math.max(latency * 2, 1));
            return wait(envelope(PLATFORM_TENANT, statusOf(entry)));
        },
    });
    const summaryOf = (tenantId) => {
        const t = tenantOf(world, tenantId);
        if (!t)
            throw failure('Summary_get', 404, 'notFound', 'Not found');
        return { ...t.billing, seats: { ...t.billing.seats } };
    };
    const billingClient = mockClient('billing', {
        Summary_get: async () => {
            const { tenantId } = tenantCaller('Summary_get');
            return wait(envelope(tenantId, summaryOf(tenantId)));
        },
        Checkouts_create: async ({ body }) => {
            const { tenantId } = tenantAdmin('Checkouts_create');
            const t = tenantOrFail('Checkouts_create', tenantId);
            if (body.packageId && t.entitlement && body.packageId !== t.entitlement.packageId)
                throw failure('Checkouts_create', 409, 'conflict', 'The package must match the one on record');
            const sessionId = `cs_mock_${world.nextId('s')}`;
            const success = body.successPath ?? '/settings/billing?checkout=success';
            // The mock "Stripe" is a page in this app that flips billing to active and comes back.
            const url = new URL(`${checkoutOrigin}/pay`);
            url.searchParams.set('tenant', tenantId);
            url.searchParams.set('seats', String(body.seats ?? 1));
            url.searchParams.set('return', `${success}${success.includes('?') ? '&' : '?'}session_id=${sessionId}`);
            return wait(envelope(tenantId, {
                checkoutUrl: url.toString(),
                sessionId,
                expiresAt: new Date(Date.now() + 30 * 60_000).toISOString(),
            }));
        },
        Portals_create: async ({ body }) => {
            const { tenantId } = tenantAdmin('Portals_create');
            const url = new URL(`${checkoutOrigin}/portal`);
            url.searchParams.set('tenant', tenantId);
            url.searchParams.set('return', body.returnPath ?? '/settings/billing');
            return wait(envelope(tenantId, { portalUrl: url.toString() }));
        },
        Subscriptions_list: async ({ query } = {}) => {
            platformAdmin('Subscriptions_list');
            const items = world.subscriptions.filter((s) => !query?.status || s.status === query.status);
            return wait(page(PLATFORM_TENANT, items.map((s) => ({ ...s }))));
        },
        Subscriptions_get: async ({ params }) => {
            platformAdmin('Subscriptions_get');
            const s = world.subscriptions.find((x) => x.tenantId === params.tenantId);
            if (!s)
                throw failure('Subscriptions_get', 404, 'notFound', 'Not found');
            return wait(envelope(PLATFORM_TENANT, { ...s }));
        },
        Subscriptions_adjust: async ({ params, body }) => {
            platformAdmin('Subscriptions_adjust');
            const s = world.subscriptions.find((x) => x.tenantId === params.tenantId);
            if (!s)
                throw failure('Subscriptions_adjust', 404, 'notFound', 'Not found');
            if (body.seats !== undefined)
                s.seats = { ...s.seats, licensed: body.seats };
            if (body.cancelAtPeriodEnd !== undefined)
                s.cancelAtPeriodEnd = body.cancelAtPeriodEnd;
            s.updatedAt = new Date().toISOString();
            const t = tenantOf(world, s.tenantId);
            if (t) {
                t.billing.seats.licensed = s.seats.licensed;
                t.billing.cancelAtPeriodEnd = s.cancelAtPeriodEnd;
            }
            return wait(envelope(PLATFORM_TENANT, { ...s }));
        },
    });
    // Notices from platform staff, as engagement serves them to the caller.
    const noticeOf = (n, sub) => ({
        noticeId: n.noticeId,
        tenantId: n.tenantId,
        severity: n.severity,
        title: n.title,
        body: n.body,
        ...(n.link ? { link: n.link } : {}),
        publishedAt: n.publishedAt,
        ...(n.expiresAt ? { expiresAt: n.expiresAt } : {}),
        ...(n.acks[sub] ? { acknowledgedAt: n.acks[sub] } : {}),
    });
    const noticesFor = (tenantId, sub) => (world.notices ?? []).filter((n) => n.tenantId === tenantId &&
        !n.withdrawnAt &&
        (n.sub === undefined || n.sub === sub) &&
        (n.expiresAt === undefined || Date.parse(n.expiresAt) > Date.now()));
    const engagementClient = mockClient('engagement', {
        Notices_list: async () => {
            const { principal, tenantId } = tenantCaller('Notices_list');
            const items = noticesFor(tenantId, principal.sub)
                .map((n) => noticeOf(n, principal.sub))
                .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
            return wait(page(tenantId, items));
        },
        Notices_acknowledge: async ({ params }) => {
            const { principal, tenantId } = tenantCaller('Notices_acknowledge');
            const n = noticesFor(tenantId, principal.sub).find((x) => x.noticeId === params.noticeId);
            if (!n)
                throw failure('Notices_acknowledge', 404, 'notFound', 'Not found');
            n.acks[principal.sub] ??= new Date().toISOString();
            return wait(envelope(tenantId, noticeOf(n, principal.sub)));
        },
    });
    return {
        identity: identityClient,
        platform: platformClient,
        billing: billingClient,
        engagement: engagementClient,
        public: { platform: platformClient },
    };
}
/** The mock's equivalent of Stripe flipping a subscription on (called by the mock checkout page). */
export function mockActivateBilling(world, tenantId, seats) {
    const t = tenantOf(world, tenantId);
    if (!t)
        return;
    const now = new Date();
    const end = new Date(now.getTime() + 30 * 86_400_000).toISOString();
    t.status = t.status === 'locked' ? 'active' : t.status;
    t.billing = {
        ...t.billing,
        status: 'active',
        seats: { licensed: Math.max(seats, t.billing.seats.used), used: t.billing.seats.used },
        currentPeriodEnd: end,
        cancelAtPeriodEnd: false,
        portalAvailable: true,
        lastInvoice: {
            invoiceId: `in_mock${world.nextId('i')}`,
            status: 'paid',
            amountDueCents: 0,
            amountPaidCents: 0,
            currency: 'usd',
            paidAt: now.toISOString(),
            attemptCount: 1,
        },
    };
    const s = world.subscriptions.find((x) => x.tenantId === tenantId);
    if (s) {
        s.status = 'active';
        s.seats = { ...t.billing.seats };
        s.currentPeriodEnd = end;
        s.updatedAt = now.toISOString();
    }
    else {
        world.subscriptions.push({
            tenantId,
            status: 'active',
            ...(t.billing.packageId ? { packageId: t.billing.packageId } : {}),
            billingModel: t.billing.billingModel,
            seats: { ...t.billing.seats },
            currentPeriodEnd: end,
            cancelAtPeriodEnd: false,
            mode: 'sandbox',
            createdAt: now.toISOString(),
            updatedAt: now.toISOString(),
        });
    }
}
/**
 * The mock's equivalent of a personal sign-up's payment landing (mock checkout page): the
 * workflow moves to provisioning, then — after `activateAfterMs` — the cell attaches the person
 * as the tenant's owner (TenantAdmin, active), billing is active and the status is `active`.
 */
export function mockPayPersonalCheckout(world, tenantId, activateAfterMs = 1_500) {
    const s = world.signups.get(tenantId);
    const t = tenantOf(world, tenantId);
    if (!s || !t || s.status !== 'pendingPayment')
        return;
    s.status = 'provisioning';
    delete s.checkoutUrl;
    t.status = 'provisioning';
    setTimeout(() => {
        const now = new Date().toISOString();
        t.status = 'active';
        t.updatedAt = now;
        mockActivateBilling(world, tenantId, 1);
        if (s.ownerSub &&
            !world.memberships.some((m) => m.tenantId === tenantId && m.sub === s.ownerSub))
            world.memberships.push({
                tenantId,
                tenantName: t.name,
                tier: 'pool',
                sub: s.ownerSub,
                roles: ['TenantAdmin'],
                grants: [],
                createdAt: now,
                status: 'active',
            });
        s.status = 'active';
    }, activateAfterMs);
}
const DAY_MS = 24 * 3600 * 1000;
const RESERVED = new Set(['admin', 'api', 'app', 'www', 'platform', 'sprocket', 'login', 'signup']);
/** A 26-char Crockford base32 id, enough to satisfy the contract's RecordId pattern. */
function ulidLike(world) {
    const alphabet = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
    const seed = `${Date.now().toString(32)}${world.nextId('')}`.toUpperCase();
    let out = '';
    for (let i = 0; i < 26; i++) {
        const c = seed.charCodeAt(i % seed.length) + i;
        out += alphabet[c % alphabet.length];
    }
    return out;
}
const metered = (cost, input, output, calls) => [
    { metric: 'assistant.cost', unit: 'usd-micro', quantity: cost, records: calls },
    { metric: 'assistant.input-tokens', unit: 'tokens', quantity: input, records: calls },
    { metric: 'assistant.output-tokens', unit: 'tokens', quantity: output, records: calls },
];
/** The assistant's metered usage the mock control plane has summed (#16): month and today. */
const MOCK_USAGE = {
    harbor: {
        month: metered(168_420_000, 41_230_000, 6_120_000, 1_284),
        day: metered(4_210_000, 1_020_000, 151_000, 37),
    },
};
/**
 * Fill a client type from a partial implementation: any operation no screen calls yet answers
 * `501 notImplemented` through the same `ContractError` the real client would throw.
 */
function mockClient(context, impl) {
    return new Proxy(impl, {
        get(target, prop, receiver) {
            const value = Reflect.get(target, prop, receiver);
            if (value !== undefined || typeof prop !== 'string')
                return value;
            if (prop === 'then')
                return undefined;
            return () => Promise.reject(failure(`${context}.${prop}`, 501, 'internal', `${prop} is not in the mock yet`));
        },
    });
}
//# sourceMappingURL=mock-api.js.map